import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, Loader2, ArrowLeft, ShieldCheck, Fingerprint, Smartphone, Eye, EyeOff } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { useAuth } from '@shared/contexts/AuthContext';
import { signIn as authServiceSignIn, requestPasswordReset, verifyMfa, verifyWebAuthn } from '@shared/services/authService';
import { ROUTES } from '@shared/config/routes';
import { useToast } from '@shared/components/ui/use-toast';

/**
 * Shared two-panel layout — defined at module scope so its identity never
 * changes between renders (avoids unmounting child inputs on each keystroke).
 */
const Layout = ({ children }) => (
  <div className="min-h-screen flex">
    {/* Left panel */}
    <div
      className="w-full lg:w-1/3 flex items-center justify-center p-8"
      style={{
        backgroundColor: '#0f0f0f',
        backgroundImage: `
          repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.02) 10px, rgba(255,255,255,.02) 20px),
          repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(255,255,255,.02) 10px, rgba(255,255,255,.02) 20px)
        `,
      }}
    >
      <div className="w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <img src="/assets/Atlaslogo.png" alt="Logo" className="h-28 w-auto" />
        </div>
        {children}
      </div>
    </div>

    {/* Right hero panel */}
    <div
      className="hidden lg:flex lg:w-2/3 relative items-end p-12"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.45),rgba(0,0,0,0.45)), url('/assets/ATC-PHOTO-71.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="text-white">
        <h2 className="text-4xl font-bold mb-4">Site Management, Made Simple</h2>
        <p className="text-lg text-gray-200 mb-4">
          Track visitor inductions, manage site check-ins and check-outs, and ensure compliance — all in one place.
        </p>
        <p className="text-sm text-gray-400">© 2025 Depot Induction System · Powered by Apexiar</p>
      </div>
    </div>
  </div>
);

/**
 * Enterprise Login Page
 *
 * Features:
 * - Redirects to intended page after login (preserves state.from)
 * - Redirects already-authenticated users immediately
 * - Forgot password flow with email link
 * - MFA: TOTP 6-digit auto-advance + WebAuthn/passkey
 * - Human-readable error messages
 * - Show/hide password toggle
 */
const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();

  // Where to redirect after successful login — captured once so it never changes
  const fromRef = useRef(location.state?.from?.pathname || ROUTES.APP.HOME);
  const from = fromRef.current;

  // ── Already signed in — redirect immediately ───────────────────
  // (useRef only — no conditional hooks, so this ref is always called)

  // ── Form state ──────────────────────────────────────────────────
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);

  // ── View: 'login' | 'forgot' | 'forgot-sent' | 'mfa' ───────────
  const [view, setView] = useState('login');

  // ── Forgot password ─────────────────────────────────────────────
  const [resetEmail, setResetEmail]     = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // ── MFA ─────────────────────────────────────────────────────────
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode]           = useState(['', '', '', '', '', '']);
  const [mfaLoading, setMfaLoading]     = useState(false);
  const [mfaMethod, setMfaMethod]       = useState(null); // 'totp' | 'webauthn'
  const mfaRefs = useRef([]);

  // ── Focus first MFA input when view switches ────────────────────
  useEffect(() => {
    if (view === 'mfa') setTimeout(() => mfaRefs.current[0]?.focus(), 50);
  }, [view]);

  // ── Already signed in — redirect immediately (after all hooks) ──
  if (user) {
    return <Navigate to={from} replace />;
  }

  // ── Humanise Supabase/authService errors ────────────────────────
  const humaniseError = (msg = '') => {
    if (msg.includes('Invalid login credentials'))  return 'Invalid email or password.';
    if (msg.includes('Email not confirmed'))         return 'Please confirm your email address before signing in.';
    if (msg.includes('profile not found'))           return 'No account found — contact your administrator.';
    if (msg.includes('deactivated'))                 return 'Your account has been deactivated — contact your administrator.';
    if (msg.includes('Too many requests'))           return 'Too many attempts. Please wait a moment and try again.';
    return msg || 'Something went wrong. Please try again.';
  };

  // ── Sign in ─────────────────────────────────────────────────────
  const handleSignIn = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const result = await authServiceSignIn(email, password);

      if (result.mfaRequired) {
        setMfaChallenge({
          factorId:         result.factorId,
          factorType:       result.factorType,
          challengeId:      result.challengeId,
          availableMethods: result.availableMethods,
        });
        setMfaMethod(result.factorType || 'totp');
        setView('mfa');
        return;
      }

      // Force password change on first login
      if (result.requiresPasswordChange) {
        navigate('/change-password', { replace: true });
        return;
      }

      // AuthContext onAuthStateChange will set user → useEffect above redirects.
      // Explicit navigate here covers the case where user state is already set.
      navigate(from, { replace: true });
    } catch (err) {
      setError(humaniseError(err.message));
    } finally {
      setLoading(false);
    }
  };

  // ── Forgot password ─────────────────────────────────────────────
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    try {
      await requestPasswordReset(resetEmail);
      setView('forgot-sent');
    } catch (err) {
      toast({ title: 'Reset failed', description: humaniseError(err.message), variant: 'destructive' });
    } finally {
      setResetLoading(false);
    }
  };

  // ── MFA helpers ──────────────────────────────────────────────────
  const handleMfaDigit = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const next = [...mfaCode];
    next[index] = value;
    setMfaCode(next);
    if (value && index < 5) mfaRefs.current[index + 1]?.focus();
    if (value && index === 5) {
      const full = next.join('');
      if (full.length === 6) handleMfaVerify(full);
    }
  };

  const handleMfaKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !mfaCode[index] && index > 0) mfaRefs.current[index - 1]?.focus();
  };

  const handleMfaPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) { setMfaCode(pasted.split('')); handleMfaVerify(pasted); }
  };

  const handleMfaVerify = async (code = mfaCode.join('')) => {
    if (code.length !== 6) return;
    setMfaLoading(true);
    try {
      await verifyMfa(mfaChallenge.factorId, mfaChallenge.challengeId, code);
      navigate(from, { replace: true });
    } catch (err) {
      toast({ title: 'Verification failed', description: err.message || 'Invalid code.', variant: 'destructive' });
      setMfaCode(['', '', '', '', '', '']);
      mfaRefs.current[0]?.focus();
    } finally {
      setMfaLoading(false);
    }
  };

  const handleWebAuthnVerify = async () => {
    setMfaLoading(true);
    try {
      await verifyWebAuthn(mfaChallenge.factorId, mfaChallenge.challengeId);
      navigate(from, { replace: true });
    } catch (err) {
      toast({ title: 'Passkey failed', description: err.message || 'Verification failed.', variant: 'destructive' });
    } finally {
      setMfaLoading(false);
    }
  };

  const handleSwitchMfaMethod = (method) => {
    const match = (mfaChallenge.availableMethods || []).find(m => m.type === method);
    if (!match) return;
    setMfaMethod(method);
    setMfaCode(['', '', '', '', '', '']);
    setMfaChallenge(prev => ({ ...prev, factorId: match.factorId, factorType: method }));
  };

  // ── MFA screen ───────────────────────────────────────────────────
  if (view === 'mfa') {
    const isWebAuthn  = mfaMethod === 'webauthn';
    const hasMultiple = (mfaChallenge?.availableMethods?.length || 0) > 1;

    return (
      <Layout>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
          <div className="mb-8 text-center">
            <div className={`mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center ${isWebAuthn ? 'bg-purple-600' : 'bg-green-600'}`}>
              {isWebAuthn ? <Fingerprint className="w-8 h-8 text-white" /> : <ShieldCheck className="w-8 h-8 text-white" />}
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Two-Factor Authentication</h1>
            <p className="text-gray-400 text-sm">
              {isWebAuthn ? 'Verify with your passkey or security key' : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          {isWebAuthn ? (
            <Button
              onClick={handleWebAuthnVerify}
              disabled={mfaLoading}
              className="w-full h-14 text-lg font-semibold bg-purple-600 hover:bg-purple-700 text-white"
            >
              {mfaLoading
                ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Waiting for device…</>
                : <><Fingerprint className="mr-2 h-5 w-5" />Verify with Passkey</>}
            </Button>
          ) : (
            <>
              <div className="flex justify-center gap-2 mb-6">
                {mfaCode.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => (mfaRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleMfaDigit(i, e.target.value)}
                    onKeyDown={e => handleMfaKeyDown(i, e)}
                    onPaste={handleMfaPaste}
                    disabled={mfaLoading}
                    className="w-12 h-14 text-center text-2xl font-bold rounded-lg border-2 bg-gray-800 text-white focus:outline-none transition-colors"
                    style={{ borderColor: digit ? 'hsl(45 100% 50%)' : 'hsl(0 0% 30%)' }}
                  />
                ))}
              </div>
              <Button
                onClick={() => handleMfaVerify()}
                disabled={mfaLoading || mfaCode.join('').length !== 6}
                className="w-full h-14 text-lg font-semibold border-2"
                style={{ backgroundColor: 'hsl(45 100% 50%)', color: 'hsl(0 0% 10%)', borderColor: 'hsl(45 100% 50%)' }}
              >
                {mfaLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Verifying…</> : 'Verify Code'}
              </Button>
            </>
          )}

          {hasMultiple && (
            <div className="mt-4 flex justify-center gap-2">
              {mfaChallenge.availableMethods.filter(m => m.type !== mfaMethod).map(m => (
                <Button key={m.type} variant="outline" size="sm" onClick={() => handleSwitchMfaMethod(m.type)} disabled={mfaLoading} className="border-gray-600 text-gray-300 hover:text-white">
                  {m.type === 'webauthn'
                    ? <><Fingerprint className="w-4 h-4 mr-1.5" />Use Passkey</>
                    : <><Smartphone className="w-4 h-4 mr-1.5" />Use App Code</>}
                </Button>
              ))}
            </div>
          )}

          <button
            onClick={() => { setView('login'); setMfaChallenge(null); setMfaCode(['', '', '', '', '', '']); }}
            disabled={mfaLoading}
            className="mt-5 flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mx-auto transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to login
          </button>
        </motion.div>
      </Layout>
    );
  }

  // ── Forgot password — sent ────────────────────────────────────────
  if (view === 'forgot-sent') {
    return (
      <Layout>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-green-600 flex items-center justify-center">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Check your email</h1>
          <p className="text-gray-400 mb-6">
            We sent a reset link to{' '}
            <span className="text-white font-medium">{resetEmail}</span>.<br />
            The link expires in 1 hour.
          </p>
          <Button
            onClick={() => { setView('login'); setResetEmail(''); }}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:text-white"
          >
            Back to login
          </Button>
        </motion.div>
      </Layout>
    );
  }

  // ── Forgot password — form ────────────────────────────────────────
  if (view === 'forgot') {
    return (
      <Layout>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-white mb-2">Reset Password</h1>
            <p className="text-gray-400">Enter your email and we'll send a reset link.</p>
          </div>

          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="reset-email" className="text-gray-300 text-base">Email Address</Label>
              <Input
                id="reset-email"
                type="email"
                placeholder="name@company.com"
                value={resetEmail}
                onChange={e => setResetEmail(e.target.value)}
                required
                disabled={resetLoading}
                className="h-14 bg-gray-800 border-2 text-white placeholder:text-gray-500"
                style={{ borderColor: 'hsl(45 100% 50%)' }}
              />
            </div>
            <Button
              type="submit"
              disabled={resetLoading}
              className="w-full h-14 text-lg font-semibold border-2"
              style={{ backgroundColor: 'hsl(45 100% 50%)', color: 'hsl(0 0% 10%)', borderColor: 'hsl(45 100% 50%)' }}
            >
              {resetLoading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Sending…</> : 'Send Reset Link'}
            </Button>
          </form>

          <button
            onClick={() => setView('login')}
            className="mt-6 flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mx-auto transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to login
          </button>
        </motion.div>
      </Layout>
    );
  }

  // ── Main login form ───────────────────────────────────────────────
  return (
    <Layout>
      <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4 }}>
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-white mb-3">Welcome Back</h1>
          <p className="text-gray-400 text-lg">Enter your credentials to continue</p>
        </div>

        <form onSubmit={handleSignIn} className="space-y-5">
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-300 text-base">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="name@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={loading}
              className="h-14 bg-gray-800 border-2 text-white placeholder:text-gray-500 text-base"
              style={{ borderColor: 'hsl(45 100% 50%)' }}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-gray-300 text-base">Password</Label>
              <button
                type="button"
                onClick={() => setView('forgot')}
                className="text-sm font-medium hover:underline transition-opacity"
                style={{ color: 'hsl(45 100% 50%)' }}
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={loading}
                className="h-14 bg-gray-800 border-2 text-white placeholder:text-gray-500 text-base pr-12"
                style={{ borderColor: 'hsl(45 100% 50%)' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(p => !p)}
                tabIndex={-1}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full h-14 text-lg font-semibold border-2"
            disabled={loading}
            style={{ backgroundColor: 'hsl(45 100% 50%)', color: 'hsl(0 0% 10%)', borderColor: 'hsl(45 100% 50%)' }}
          >
            {loading ? <><Loader2 className="mr-2 h-6 w-6 animate-spin" />Signing in…</> : 'Sign In'}
          </Button>
        </form>

        <p className="mt-6 text-center text-sm text-gray-500">
          No self-registration — contact your administrator for access.
        </p>
      </motion.div>
    </Layout>
  );
};

export default LoginPage;
